import { useState } from 'react';
import { Wordmark } from '../Wordmark';

/**
 * Public landing page — what unauthenticated visitors see.
 *
 * Deliberately says nothing operational: no account names, no line status, no
 * counts. It shows what the product does — literally, in a live inspection
 * viewport that scans, boxes a defect and cycles a verdict — and hands off to the
 * login window. Serves www.thevisionqc.com once that domain is proxied here.
 */

const FEATURES = [
  {
    span: 2,
    icon: '◉',
    tone: 'var(--accent)',
    title: 'Inspect in real time',
    body: 'Every part is scored the moment it is photographed. GOOD, BAD — or UNSURE when the call is genuinely too close, so a person decides instead of a coin flip.',
  },
  {
    span: 1,
    icon: '✎',
    tone: 'var(--teal)',
    title: 'Teach it your defects',
    body: 'Operators label images with your own vocabulary — scratch, dent, contamination — each named, coloured, and carrying its own verdict.',
  },
  {
    span: 1,
    icon: '⚗',
    tone: 'var(--violet)',
    title: 'Train on your own data',
    body: 'Labelled images become a training set in one click. Activate a model per product, roll back instantly if it underperforms.',
  },
  {
    span: 1,
    icon: '⛑',
    tone: 'var(--warn)',
    title: 'Watch the floor, too',
    body: 'The same eye that grades a part watches the workplace — PPE, forklifts, fire, no-entry zones — and raises events in real time.',
  },
  {
    span: 1,
    icon: '⚙',
    tone: 'var(--good)',
    title: 'Drive the line',
    body: 'Camera, conveyor and reject flipper behind one hardware layer, with encoder-accurate timing. Runs fully virtual until the hardware arrives.',
  },
];

const STEPS = [
  { n: '01', t: 'Capture', d: 'Camera or upload — images land and are scored immediately.' },
  { n: '02', t: 'Label', d: 'Operators confirm or correct; the most uncertain images come first.' },
  { n: '03', t: 'Train', d: 'Export the labelled set, train a model, activate it for that product.' },
  { n: '04', t: 'Improve', d: 'Re-run over unjudged samples to see what it now gets right.' },
];

const LANDING_CSS = `
.lv-root { --lv-max: 1160px; background: var(--bg); min-height: 100vh; overflow-x: hidden; }
.lv-wrap { max-width: var(--lv-max); margin: 0 auto; padding: 0 24px; }

.lv-header {
  position: sticky; top: 0; z-index: 20;
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 24px; border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--bg) 72%, transparent);
  backdrop-filter: blur(12px);
}

/* Hero */
.lv-hero { position: relative; padding: 72px 0 64px; }
.lv-hero-grid { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 48px; align-items: center; }
.lv-glow {
  position: absolute; inset: -20% -10% auto -10%; height: 520px; pointer-events: none; z-index: 0;
  background:
    radial-gradient(55% 60% at 22% 30%, color-mix(in srgb, var(--accent) 30%, transparent), transparent 70%),
    radial-gradient(45% 55% at 82% 20%, color-mix(in srgb, var(--violet) 26%, transparent), transparent 70%),
    radial-gradient(40% 50% at 60% 80%, color-mix(in srgb, var(--teal) 20%, transparent), transparent 70%);
  filter: blur(20px); opacity: 0.8; animation: lv-drift 18s ease-in-out infinite alternate;
}
@keyframes lv-drift { from { transform: translate3d(-2%, 0, 0) scale(1); } to { transform: translate3d(3%, 2%, 0) scale(1.06); } }

.lv-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 700;
  color: var(--text-dim);
  border: 1px solid var(--border); border-radius: 999px; padding: 6px 12px;
  background: color-mix(in srgb, var(--bg-panel) 60%, transparent);
}
.lv-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--good); box-shadow: 0 0 0 0 color-mix(in srgb, var(--good) 60%, transparent); animation: lv-ping 2.2s ease-out infinite; }
@keyframes lv-ping { 0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--good) 55%, transparent); } 70%,100% { box-shadow: 0 0 0 8px transparent; } }

.lv-h1 { font-size: clamp(38px, 5.4vw, 64px); line-height: 1.03; font-weight: 900; letter-spacing: -0.02em; margin: 20px 0 0; }
.lv-grad {
  background: linear-gradient(100deg, var(--accent), var(--teal) 55%, var(--violet));
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.lv-sub { font-size: clamp(16px, 1.6vw, 20px); color: var(--text-dim); line-height: 1.55; max-width: 540px; margin: 22px 0 0; }
.lv-cta { display: flex; gap: 12px; margin-top: 32px; flex-wrap: wrap; }
.lv-btn-lg { padding: 14px 26px; font-size: 16px; }
.lv-btn-primary {
  background: linear-gradient(100deg, var(--accent), color-mix(in srgb, var(--violet) 70%, var(--accent)));
  border: none; color: #fff; box-shadow: 0 8px 24px color-mix(in srgb, var(--accent) 35%, transparent);
}
.lv-btn-primary:hover { filter: brightness(1.08); }

/* Inspection viewport */
.lv-scope {
  position: relative; z-index: 1; aspect-ratio: 4 / 3; border-radius: 16px;
  background: radial-gradient(120% 120% at 50% 0%, #0f1620, #0a0d12 70%);
  border: 1px solid var(--border);
  box-shadow: 0 30px 80px rgba(0,0,0,0.5), inset 0 0 0 1px color-mix(in srgb, var(--accent) 12%, transparent);
  overflow: hidden; animation: lv-float 7s ease-in-out infinite;
}
@keyframes lv-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
.lv-scope-grid {
  position: absolute; inset: 0;
  background-image: linear-gradient(color-mix(in srgb, var(--accent) 9%, transparent) 1px, transparent 1px),
                    linear-gradient(90deg, color-mix(in srgb, var(--accent) 9%, transparent) 1px, transparent 1px);
  background-size: 34px 34px; opacity: 0.5;
}
.lv-part {
  position: absolute; left: 50%; top: 50%; width: 52%; aspect-ratio: 1; transform: translate(-50%, -50%);
}
.lv-scan {
  position: absolute; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, transparent, var(--teal), transparent);
  box-shadow: 0 0 18px 3px color-mix(in srgb, var(--teal) 60%, transparent);
  animation: lv-scan 3.4s cubic-bezier(.5,0,.5,1) infinite;
}
@keyframes lv-scan { 0% { top: 8%; opacity: 0; } 12% { opacity: 1; } 88% { opacity: 1; } 100% { top: 92%; opacity: 0; } }
.lv-box {
  position: absolute; left: 56%; top: 40%; width: 26%; height: 22%;
  border: 2px dashed var(--warn); border-radius: 6px;
  box-shadow: 0 0 0 3px rgba(0,0,0,0.5); animation: lv-boxpulse 3.4s ease-in-out infinite;
}
@keyframes lv-boxpulse { 0%,20% { opacity: 0; } 45% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }
.lv-box span { position: absolute; top: -20px; left: -2px; font-size: 10px; font-weight: 700; color: #04240f; background: var(--warn); padding: 1px 6px; border-radius: 4px; white-space: nowrap; }
.lv-corner { position: absolute; width: 22px; height: 22px; border: 2px solid color-mix(in srgb, var(--accent) 70%, transparent); }
.lv-corner.tl { top: 14px; left: 14px; border-right: 0; border-bottom: 0; border-radius: 6px 0 0 0; }
.lv-corner.tr { top: 14px; right: 14px; border-left: 0; border-bottom: 0; border-radius: 0 6px 0 0; }
.lv-corner.bl { bottom: 14px; left: 14px; border-right: 0; border-top: 0; border-radius: 0 0 0 6px; }
.lv-corner.br { bottom: 14px; right: 14px; border-left: 0; border-top: 0; border-radius: 0 0 6px 0; }

.lv-verdict { position: absolute; top: 16px; right: 16px; display: grid; }
.lv-chip {
  grid-area: 1 / 1; font-family: var(--mono); font-weight: 800; font-size: 15px; letter-spacing: 1px;
  padding: 6px 12px; border-radius: 8px; border: 1px solid; backdrop-filter: blur(4px);
}
.lv-chip.good { color: var(--good); border-color: var(--good); background: color-mix(in srgb, var(--good) 16%, transparent); animation: lv-v1 10.2s infinite; }
.lv-chip.bad { color: var(--bad); border-color: var(--bad); background: color-mix(in srgb, var(--bad) 16%, transparent); animation: lv-v2 10.2s infinite; }
.lv-chip.uns { color: var(--warn); border-color: var(--warn); background: color-mix(in srgb, var(--warn) 16%, transparent); animation: lv-v3 10.2s infinite; }
@keyframes lv-v1 { 0%,28% { opacity: 1; } 33%,100% { opacity: 0; } }
@keyframes lv-v2 { 0%,33% { opacity: 0; } 38%,61% { opacity: 1; } 66%,100% { opacity: 0; } }
@keyframes lv-v3 { 0%,66% { opacity: 0; } 71%,94% { opacity: 1; } 100% { opacity: 0; } }

.lv-readout { position: absolute; left: 16px; bottom: 14px; font-family: var(--mono); font-size: 11px; color: var(--text-dim); letter-spacing: 0.5px; }
.lv-readout b { color: var(--teal); }

/* Capability strip */
.lv-strip { display: flex; flex-wrap: wrap; gap: 10px 28px; margin-top: 40px; padding-top: 24px; border-top: 1px solid var(--border); }
.lv-strip span { font-size: 13px; color: var(--text-dim); display: inline-flex; align-items: center; gap: 8px; }
.lv-strip span::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--accent); }

/* Bento */
.lv-section-label { font-size: 12px; letter-spacing: 2px; text-transform: uppercase; color: var(--text-dim); font-weight: 700; }
.lv-bento { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 18px; }
.lv-tile {
  position: relative; border-radius: 14px; padding: 22px;
  background: linear-gradient(160deg, color-mix(in srgb, var(--tile) 9%, transparent), transparent 42%), var(--bg-panel);
  border: 1px solid var(--border); border-left: 3px solid var(--tile);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.lv-tile:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(0,0,0,0.35); }
.lv-tile-ico { font-size: 24px; color: var(--tile); }
.lv-tile h3 { margin: 12px 0 8px; font-size: 18px; }
.lv-tile p { margin: 0; color: var(--text-dim); font-size: 14px; line-height: 1.55; }

/* Steps */
.lv-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 18px; position: relative; }
.lv-step { padding: 20px; border-radius: 14px; background: var(--bg-panel); border: 1px solid var(--border); }
.lv-step-n { font-family: var(--mono); font-size: 28px; font-weight: 800; background: linear-gradient(120deg, var(--accent), var(--teal)); -webkit-background-clip: text; background-clip: text; color: transparent; }

.lv-cta-band {
  margin-top: 72px; border-radius: 20px; padding: 40px; overflow: hidden; position: relative;
  border: 1px solid var(--border);
  background: radial-gradient(90% 140% at 100% 0%, color-mix(in srgb, var(--violet) 22%, transparent), transparent 60%),
             radial-gradient(80% 120% at 0% 100%, color-mix(in srgb, var(--accent) 20%, transparent), transparent 60%),
             var(--bg-panel);
}

@media (max-width: 900px) {
  .lv-hero-grid { grid-template-columns: 1fr; gap: 32px; }
  .lv-bento, .lv-steps { grid-template-columns: 1fr 1fr; }
  .lv-tile.wide { grid-column: span 2; }
}
@media (max-width: 560px) {
  .lv-bento, .lv-steps { grid-template-columns: 1fr; }
  .lv-tile.wide { grid-column: span 1; }
}
`;

export function LandingView({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="lv-root">
      <style>{LANDING_CSS}</style>

      <header className="lv-header">
        <div className="brand" style={{ padding: 0, fontSize: 22 }}>
          <Wordmark />
        </div>
        <button className="btn primary" onClick={onSignIn}>Sign in</button>
      </header>

      <div className="lv-wrap">
        {/* Hero */}
        <section className="lv-hero">
          <div className="lv-glow" />
          <div className="lv-hero-grid">
            <div style={{ position: 'relative', zIndex: 1 }}>
              <span className="lv-eyebrow"><span className="lv-dot" /> AI visual quality inspection</span>
              <h1 className="lv-h1">
                Inspection that<br />
                <span className="lv-grad">learns your product</span>
              </h1>
              <p className="lv-sub">
                thevisionqc scores every part against a model trained on your own images — and says
                when it isn’t sure, instead of guessing. From the first photo to a line that runs itself.
              </p>
              <div className="lv-cta">
                <button className="btn lv-btn-lg lv-btn-primary" onClick={onSignIn}>Sign in to your workspace</button>
                <a className="btn lv-btn-lg" href="#enquiry">Request information</a>
              </div>
              <div className="lv-strip">
                <span>Sub-second verdicts</span>
                <span>Trains on your data</span>
                <span>Runs the line</span>
                <span>Watches the floor</span>
              </div>
            </div>

            {/* Live inspection viewport */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div className="lv-scope" aria-hidden>
                <div className="lv-scope-grid" />
                <PartSvg />
                <div className="lv-box"><span>defect · 0.94</span></div>
                <div className="lv-scan" />
                <div className="lv-corner tl" /><div className="lv-corner tr" />
                <div className="lv-corner bl" /><div className="lv-corner br" />
                <div className="lv-verdict">
                  <span className="lv-chip good">✓ GOOD</span>
                  <span className="lv-chip bad">✕ BAD</span>
                  <span className="lv-chip uns">? UNSURE</span>
                </div>
                <div className="lv-readout">score <b>0.98</b> · <b>42</b> ms · model v7</div>
              </div>
            </div>
          </div>
        </section>

        {/* Bento features */}
        <section style={{ marginTop: 48 }}>
          <div className="lv-section-label">What it does</div>
          <div className="lv-bento">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className={`lv-tile${f.span === 2 ? ' wide' : ''}`}
                style={{ ['--tile' as string]: f.tone, gridColumn: `span ${f.span}` } as React.CSSProperties}
              >
                <div className="lv-tile-ico">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section style={{ marginTop: 56 }}>
          <div className="lv-section-label">How it works</div>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 800, margin: '8px 0 4px' }}>
            A loop, not a one-off install
          </h2>
          <p className="muted" style={{ marginTop: 0 }}>The system gets better the more you use it.</p>
          <div className="lv-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="lv-step">
                <div className="lv-step-n">{s.n}</div>
                <h3 style={{ margin: '8px 0 6px', fontSize: 17 }}>{s.t}</h3>
                <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Enquiry / CTA band */}
        <section id="enquiry" className="lv-cta-band">
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 32, alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 800, marginTop: 0, marginBottom: 10 }}>
                See it on your parts
              </h2>
              <p className="muted" style={{ lineHeight: 1.6, marginBottom: 0 }}>
                Tell us what you make and we’ll show you what it would catch. Already have a workspace?
                Use <strong>Sign in</strong> at the top.
              </p>
            </div>
            <EnquiryForm />
          </div>
        </section>
      </div>

      <footer className="muted" style={{ borderTop: '1px solid var(--border)', padding: '24px', fontSize: 13, marginTop: 64, textAlign: 'center' }}>
        © {new Date().getFullYear()} thevisionqc — AI-based visual quality inspection.
      </footer>
    </div>
  );
}

/** The part under inspection — a stylised bearing, with a highlighted flaw the box lands on. */
function PartSvg() {
  return (
    <svg className="lv-part" viewBox="0 0 100 100" fill="none" aria-hidden>
      <defs>
        <radialGradient id="lv-metal" cx="42%" cy="34%" r="70%">
          <stop offset="0%" stopColor="#3a4757" />
          <stop offset="60%" stopColor="#232c37" />
          <stop offset="100%" stopColor="#161c24" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="url(#lv-metal)" stroke="#3a4757" strokeWidth="0.6" />
      <circle cx="50" cy="50" r="34" fill="none" stroke="#4a5a6d" strokeWidth="1.2" />
      <circle cx="50" cy="50" r="18" fill="#10151c" stroke="#4a5a6d" strokeWidth="1.2" />
      {[...Array(12)].map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        return <circle key={i} cx={50 + 26 * Math.cos(a)} cy={50 + 26 * Math.sin(a)} r="3.4" fill="#2a333f" stroke="#54657a" strokeWidth="0.5" />;
      })}
      {/* the flaw */}
      <path d="M64 40 q6 4 3 12" stroke="var(--warn)" strokeWidth="1.6" fill="none" opacity="0.9" />
    </svg>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', website: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name || !form.email) return;
    setState('sending');
    setError(null);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'admin-landing' }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({ message: 'Something went wrong' }));
        throw new Error(b.message ?? 'Something went wrong');
      }
      setState('sent');
    } catch (e) {
      setError((e as Error).message);
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <div className="panel">
        <strong style={{ color: 'var(--good)' }}>Thank you — message received.</strong>
        <p className="muted" style={{ marginBottom: 0 }}>We’ll be in touch shortly.</p>
      </div>
    );
  }

  return (
    <div className="panel grid" style={{ gap: 12 }}>
      <div>
        <label>Name *</label>
        <input value={form.name} onChange={set('name')} style={{ width: '100%' }} />
      </div>
      <div>
        <label>Email *</label>
        <input type="email" value={form.email} onChange={set('email')} style={{ width: '100%' }} />
      </div>
      <div>
        <label>Company</label>
        <input value={form.company} onChange={set('company')} style={{ width: '100%' }} />
      </div>
      <div>
        <label>What do you produce?</label>
        <textarea
          value={form.message}
          onChange={set('message')}
          rows={4}
          style={{
            width: '100%',
            background: 'var(--bg)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 15,
            fontFamily: 'inherit',
          }}
        />
      </div>
      {/* Honeypot — hidden from people, irresistible to bots. */}
      <input
        value={form.website}
        onChange={set('website')}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
      />
      <button
        className="btn primary"
        onClick={submit}
        disabled={state === 'sending' || !form.name || !form.email}
      >
        {state === 'sending' ? 'Sending…' : 'Send enquiry'}
      </button>
      {error && <div style={{ color: 'var(--bad)' }}>{error}</div>}
    </div>
  );
}
